import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { CustomEntity, V3GameProfile } from "../../entities/customGame";
import {
  getCustomList,
  getV3GameProfiles,
  getV3GameTags,
  patchV3GameEntityEnabled,
  patchV3GameProfileInTable,
} from "./thunks";
import { ResponseMetaData, ResponseObjectList } from "../../entities/global";

interface V3GamesTableState {
  activeSystemName: string;
  loading: boolean;
  saveLoading: boolean;
  needUpdateEntityList: boolean;
  needUpdateProfileList: boolean;
  needUpdateTagsList: boolean;
  tagsLoading: boolean;
  entityList: CustomEntity[];
  entityMeta: ResponseMetaData;
  profileMeta: ResponseMetaData;
  profileList: V3GameProfile[];
  tags: string[];
}

const initialState: V3GamesTableState = {
  activeSystemName: "",
  loading: false,
  saveLoading: false,
  entityList: [],
  needUpdateEntityList: true,
  needUpdateProfileList: true,
  needUpdateTagsList: true,
  tagsLoading: false,
  profileList: [],
  entityMeta: {
    limit: 250,
    page: 1,
    pages: 1,
    total: 0,
  },
  profileMeta: {
    limit: 250,
    page: 1,
    pages: 1,
    total: 0,
  },
  tags: [],
};

export const v3GamesTableSlice = createSlice({
  name: "v3_games_table",
  initialState,
  reducers: {
    handlerNeedUpdate: (state: Draft<V3GamesTableState>) => {
      state.needUpdateEntityList = true;
    },
    handlerNeedUpdateProfiles: (state: Draft<V3GamesTableState>) => {
      state.needUpdateProfileList = true;
    },
    handlerNeedTagsUpdate: (state: Draft<V3GamesTableState>) => {
      state.needUpdateTagsList = true;
    },
  },
  extraReducers: (builder) => {
    // getV3GameTags
    builder.addCase(getV3GameTags.pending, (state) => {
      state.tags = [];
      state.needUpdateTagsList = false;
      state.tagsLoading = true;
    });
    builder.addCase(
      getV3GameTags.fulfilled,
      (state, action: PayloadAction<string[]>) => {
        state.tags = action.payload;

        state.tagsLoading = false;
      }
    );

    builder.addCase(getV3GameTags.rejected, (state) => {
      state.tags = [];
      state.needUpdateTagsList = false;
      state.tagsLoading = false;
    });
    // getCustomList
    builder.addCase(getCustomList.pending, (state) => {
      state.loading = true;
      state.entityList = [];
      state.entityMeta = {
        limit: 250,
        page: 1,
        pages: 1,
        total: 0,
      };
      state.needUpdateEntityList = false;
    });
    builder.addCase(
      getCustomList.fulfilled,
      (state, action: PayloadAction<ResponseObjectList<CustomEntity[]>>) => {
        const { data, ...meta } = action.payload;
        state.entityList = data;
        state.entityMeta = meta;
        state.loading = false;
      }
    );
    builder.addCase(getCustomList.rejected, (state) => {
      state.loading = false;
    });
    // getV3GameProfiles
    builder.addCase(getV3GameProfiles.pending, (state, { meta }) => {
      state.activeSystemName = meta.arg.systemName;
      state.loading = true;
      state.needUpdateProfileList = false;
    });
    builder.addCase(getV3GameProfiles.fulfilled, (state, action) => {
      const { data, ...meta } = action.payload;
      state.profileMeta = meta;
      state.profileList = data;
      state.loading = false;
    });
    builder.addCase(getV3GameProfiles.rejected, (state) => {
      state.loading = false;
    });
    // patchV3GameProfileInTable
    builder.addCase(patchV3GameProfileInTable.pending, (state) => {
      state.saveLoading = true;
    });
    builder.addCase(patchV3GameProfileInTable.fulfilled, (state, action) => {
      if (
        state.profileList.length !== 0 &&
        state.profileList.find((profile) => profile.id === action.payload.id)
      ) {
        state.profileList = state.profileList.map((profile) => {
          if (profile.id === action.payload.id) return action.payload;
          else return profile;
        });
      }
      state.saveLoading = false;
    });
    builder.addCase(patchV3GameProfileInTable.rejected, (state) => {
      state.saveLoading = false;
    });
    // patchV3GameEntityEnabled
    builder.addCase(patchV3GameEntityEnabled.fulfilled, (state, action) => {
      if (action.payload) {
        state.entityList = state.entityList.map((e) =>
          e.id === action.payload!.id ? action.payload! : e
        );
      }
    });
  },
});

export const {
  handlerNeedUpdate,
  handlerNeedUpdateProfiles,
  handlerNeedTagsUpdate,
} = v3GamesTableSlice.actions;

export default v3GamesTableSlice.reducer;
